import { differenceInDays, isValid, parseISO } from "date-fns";

type Deltas = Record<
  string,
  {
    type: "added" | "removed" | "modified";
    valueA?: unknown;
    valueB?: unknown;
    summary?: string;
  }
>;

type GetDeltasResult = {
  deltas: Deltas;
  deletionsByParentPath: Record<string, unknown[]>;
};

/**
 * Returns an object of deltas between two values.
 *
 * @param valueA - The first value to compare
 * @param valueB - The second value to compare
 * @returns An object containing deltas and a map of deletions by parent path.
 */
const getDeltas = (valueA: unknown, valueB: unknown): GetDeltasResult => {
  const result: GetDeltasResult = {
    deltas: {},
    deletionsByParentPath: {},
  };

  const getDeltasWithPath = (a: unknown, b: unknown, path: string[] = []) => {
    if (a === b) {
      return;
    }

    const pathKey = path.join(".");

    if (a === undefined || a === null) {
      if (b !== undefined && b !== null) {
        result.deltas[pathKey] = { type: "added", valueB: b };
      }
      return;
    }
    if (b === undefined || b === null) {
      result.deltas[pathKey] = { type: "removed", valueA: a };
      return;
    }

    if (Array.isArray(a) && Array.isArray(b)) {
      const parentPath = path.join(".");

      const mapA = new Map(a.map((item) => [item.id, item]));
      const mapB = new Map(b.map((item) => [item.id, item]));

      const addedItems = b.filter((itemB) => !mapA.has(itemB.id));
      const removedItems = a.filter((itemA) => !mapB.has(itemA.id));

      if (addedItems.length > 0 || removedItems.length > 0) {
        const summaryParts = [];
        if (addedItems.length > 0) {
          summaryParts.push(`+${addedItems.length} rows`);
        }
        if (removedItems.length > 0) {
          summaryParts.push(`-${removedItems.length} rows`);
        }
        result.deltas[parentPath] = {
          type: "modified",
          valueA: a,
          valueB: b,
          summary: `(${summaryParts.join(", ")})`,
        };
      }

      for (const [id, itemA] of mapA.entries()) {
        const itemPath = [...path, `id:${id}`];
        if (!mapB.has(id)) {
          result.deltas[itemPath.join(".")] = {
            type: "removed",
            valueA: itemA,
          };
          if (!result.deletionsByParentPath[parentPath]) {
            result.deletionsByParentPath[parentPath] = [];
          }
          result.deletionsByParentPath[parentPath].push(itemA);
        } else {
          const itemB = mapB.get(id);
          getDeltasWithPath(itemA, itemB, itemPath);
        }
      }

      for (const [id, itemB] of mapB.entries()) {
        if (!mapA.has(id)) {
          const itemPath = [...path, `id:${id}`].join(".");
          result.deltas[itemPath] = {
            type: "added",
            valueB: itemB,
          };
        }
      }

      return;
    }

    if (
      typeof a === "object" &&
      typeof b === "object" &&
      a !== null &&
      b !== null
    ) {
      const allKeys = new Set([...Object.keys(a), ...Object.keys(b)]);

      for (const key of allKeys) {
        const valA = a[key];
        const valB = b[key];
        getDeltasWithPath(valA, valB, [...path, key]);
      }

      return;
    }

    if (a !== b) {
      let summary: string | undefined = undefined;

      if (typeof a === "string" && typeof b === "string") {
        const dateA = parseISO(a);
        const dateB = parseISO(b);
        if (isValid(dateA) && isValid(dateB)) {
          const diff = differenceInDays(dateB, dateA);
          if (diff !== 0) {
            summary = `(${diff > 0 ? "+" : ""}${diff} day${
              Math.abs(diff) === 1 ? "" : "s"
            })`;
          }
        }
      } else if (typeof a === "number" && typeof b === "number") {
        const diff = b - a;
        summary = `(${diff > 0 ? "+" : ""}${diff})`;
      }

      result.deltas[pathKey] = {
        type: "modified",
        valueA: a,
        valueB: b,
        summary,
      };
    }
  };

  getDeltasWithPath(valueA, valueB);
  return result;
};

export const Generic = {
  getDeltas,
};
